import PropTypes from 'prop-types';
import { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { LoyaltyService } from 'utils/api-service';
import { subscribe, unsubscribe } from 'utils/global-events';

const MembershipContext = createContext();

export const MembershipContextProvider = ({ children }) => {
  const [membership, setMembership] = useState(null);
  const [patientMembership, setPatientMembership] = useState(null);
  const [patientCard, setPatientCard] = useState(null);
  const [membershipCards, setMembershipCards] = useState([]);
  const [upgradeableCardTypes, setUpgradeableCardTypes] = useState([]);

  const member = useSelector(state => state.member);

  const LS = useMemo(() => new LoyaltyService(), []);

  const getCardTypeList = useCallback(async () => {
    const { data, isSuccess } = await LS.getCardTypeList();
    if (isSuccess) {
      let initialCard = data.find(item => item.ishaveThisCard)
        ? data.find(item => item.ishaveThisCard)
        : data.find(item => item.isDefault);
      setPatientCard(initialCard);
      const _upgradeableCardTypes = data.filter(item => item.messageViewType != 0);
      setMembershipCards(data);
      setUpgradeableCardTypes(_upgradeableCardTypes);
    }
  }, [LS]);

  const patientCardInformation = useCallback(async () => {
    console.log('patientCardInformation');
    const { data, isSuccess } = await LS.patientCardInformation();
    if (isSuccess) {
      if (data?.loyalty) {
        setMembership(data?.loyalty);
        if (data?.loyalty?.isMember && data?.loyalty?.usabilityStatus) {
          getCardTypeList();
        }
      }

      if (data?.patient) {
        const colors = data?.patient?.cardColor.split(';').map(color => `#${color}`);
        const colorStops = colors.map((color, index) => {
          let position;
          if (index === 0) {
            position = 10;
          } else if (index === colors.length - 1) {
            position = 80;
          } else {
            position = 30;
          }
          return `${color} ${position}%`;
        });
        const bgGradientRight = `linear-gradient(to right, ${colorStops.join(', ')})`;
        const bgGradientBottom = `linear-gradient(to bottom, ${colorStops.join(', ')})`;
        const bgGradientWithDegree = `linear-gradient(225deg, ${colors.join(', ')})`;
        const color = colors[2];

        const _patientMembership = {
          ...data?.patient,
          styles: {
            bgGradientRight,
            bgGradientBottom,
            bgGradientWithDegree,
            color,
          },
        };

        setPatientMembership(_patientMembership);
      }
    }
  }, [LS, getCardTypeList]);

  useEffect(() => {
    if (member != null) {
      patientCardInformation();
    }
    subscribe('onPatientCardInformmationRefresh', () => {
      patientCardInformation();
    });

    return () => {
      unsubscribe('onPatientCardInformmationRefresh');
    };
  }, [member, patientCardInformation]);

  const contextValue = useMemo(
    () => ({
      membership,
      patientMembership,
      patientCard,
      membershipCards,
      upgradeableCardTypes,
    }),
    [membership, patientMembership, membershipCards, upgradeableCardTypes, patientCard]
  );

  return <MembershipContext.Provider value={contextValue}>{children}</MembershipContext.Provider>;
};

MembershipContextProvider.propTypes = {
  children: PropTypes.node,
};

export const useMembershipContext = () => {
  return useContext(MembershipContext);
};
